/**
 * Component Class Utilities
 * Reusable Tailwind class combinations for consistent component styling
 */

/**
 * Button Classes
 */
export const buttonClasses = {
  // Base button styles (applied to all buttons)
  base: 'rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2',

  // Button variants
  primary: 'bg-blue-600 hover:bg-blue-700 text-white shadow-md focus:ring-blue-500',
  secondary: 'border border-gray-300 text-gray-700 hover:bg-gray-100 bg-white focus:ring-gray-300',
  danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
  success: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500',
  warning: 'bg-yellow-600 hover:bg-yellow-700 text-white focus:ring-yellow-500',
  ghost: 'text-gray-700 hover:bg-gray-100 focus:ring-gray-300',

  // Button sizes
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-lg',

  // Icon buttons
  iconSm: 'p-2 text-sm',
  iconMd: 'p-2.5 text-base',
  iconLg: 'p-3 text-lg',
};

/**
 * Helper function to combine button classes
 */
export function getButtonClass(variant: keyof typeof buttonClasses = 'primary', size: 'sm' | 'md' | 'lg' = 'md') {
  const sizeClass = buttonClasses[size];
  const variantClass = typeof variant === 'string' && variant in buttonClasses ? buttonClasses[variant] : buttonClasses.primary;

  return `${buttonClasses.base} ${variantClass} ${sizeClass}`;
}

/**
 * Card Classes
 */
export const cardClasses = {
  base: 'bg-white border border-gray-200 rounded-xl shadow-md',
  hover: 'bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-shadow cursor-pointer',
  interactive: 'bg-white border border-gray-200 rounded-xl shadow-md hover:border-blue-300 transition-colors cursor-pointer',
  padding: 'p-6',
  paddingLg: 'p-8',
};

/**
 * Input Classes
 */
export const inputClasses = {
  base: 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors',
  error: 'border-red-300 focus:ring-red-500',
  success: 'border-green-300 focus:ring-green-500',
};

/**
 * Badge Classes
 */
export const badgeClasses = {
  base: 'px-2 py-1 rounded-full text-xs font-semibold border inline-flex items-center gap-1',

  // Status variants
  success: 'bg-green-100 text-green-800 border-green-200',
  danger: 'bg-red-100 text-red-800 border-red-200',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  info: 'bg-blue-100 text-blue-800 border-blue-200',
  neutral: 'bg-gray-100 text-gray-800 border-gray-200',

  // Instance/Job status specific
  running: 'bg-green-100 text-green-800 border-green-200',
  stopped: 'bg-red-100 text-red-800 border-red-200',
  starting: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  stopping: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  queued: 'bg-blue-100 text-blue-800 border-blue-200',
  processing: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
};

/**
 * Helper function to get badge class
 */
export function getBadgeClass(variant: keyof typeof badgeClasses = 'neutral') {
  const variantClass = variant in badgeClasses ? badgeClasses[variant] : badgeClasses.neutral;
  return `${badgeClasses.base} ${variantClass}`;
}

/**
 * Navigation Classes
 */
export const navClasses = {
  item: {
    base: 'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
    active: 'bg-blue-600 text-white shadow-md',
    inactive: 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
  },
};

/**
 * Modal Classes
 */
export const modalClasses = {
  overlay: 'fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4',
  container: 'bg-white rounded-xl shadow-2xl w-full mx-4',
  containerSm: 'bg-white rounded-xl shadow-2xl max-w-sm w-full mx-4',
  containerMd: 'bg-white rounded-xl shadow-2xl max-w-md w-full mx-4',
  containerLg: 'bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4',
  containerXl: 'bg-white rounded-xl shadow-2xl max-w-xl w-full mx-4',
  container2xl: 'bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4',
  container3xl: 'bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4',
  container4xl: 'bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4',
  container5xl: 'bg-white rounded-xl shadow-2xl max-w-5xl w-full mx-4',
  container6xl: 'bg-white rounded-xl shadow-2xl max-w-6xl w-full mx-4',

  header: 'px-6 py-4 border-b border-gray-200',
  body: 'px-6 py-4',
  footer: 'px-6 py-4 border-t border-gray-200 bg-gray-50',
};

/**
 * Table Classes
 */
export const tableClasses = {
  wrapper: 'overflow-x-auto',
  table: 'min-w-full divide-y divide-gray-200',
  thead: 'bg-gray-50',
  th: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
  tbody: 'bg-white divide-y divide-gray-200',
  tr: 'hover:bg-gray-50 transition-colors',
  td: 'px-6 py-4 whitespace-nowrap text-sm text-gray-900',

  // Interactive table
  trClickable: 'hover:bg-gray-50 cursor-pointer transition-colors',
  trSelected: 'bg-blue-50',
};

/**
 * Form Classes
 */
export const formClasses = {
  label: 'block text-sm font-medium text-gray-700 mb-2',
  helperText: 'text-xs text-gray-500 mt-1',
  errorText: 'text-xs text-red-600 mt-1',
  fieldWrapper: 'space-y-4',
};

/**
 * Loading Spinner Classes
 */
export const spinnerClasses = {
  sm: 'inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent',
  md: 'inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-current border-r-transparent',
  lg: 'inline-block h-8 w-8 animate-spin rounded-full border-2 border-solid border-current border-r-transparent',
  xl: 'inline-block h-12 w-12 animate-spin rounded-full border-2 border-solid border-blue-600 border-r-transparent',
};

/**
 * Alert/Notification Classes
 */
export const alertClasses = {
  base: 'px-4 py-3 rounded-lg border',
  success: 'bg-green-50 border-green-200 text-green-700',
  error: 'bg-red-50 border-red-200 text-red-700',
  warning: 'bg-yellow-50 border-yellow-200 text-yellow-700',
  info: 'bg-blue-50 border-blue-200 text-blue-700',
};

/**
 * Header Card Classes (for page headers)
 */
export const headerCardClasses = {
  wrapper: 'bg-white border border-gray-200 rounded-xl shadow-md mb-8 overflow-hidden',
  accentBar: 'h-1.5 bg-blue-600',
  content: 'p-6',
  iconContainer: 'flex-shrink-0 w-14 h-14 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600',
  title: 'text-3xl font-bold text-gray-900',
  description: 'text-gray-600',
};

/**
 * Utility function to combine classes
 */
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

export default {
  button: buttonClasses,
  card: cardClasses,
  input: inputClasses,
  badge: badgeClasses,
  nav: navClasses,
  modal: modalClasses,
  table: tableClasses,
  form: formClasses,
  spinner: spinnerClasses,
  alert: alertClasses,
  headerCard: headerCardClasses,
  getButtonClass,
  getBadgeClass,
  cn,
};
